import React from "react";
import { Button, Alert, CircularProgress, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import type translations from "../translations/translations.json";
import PasswordInput from "./common/PasswordInput";
import LoadingState from "./common/LoadingState";
import { validatePassword, doPasswordsMatch } from "../utils/passwordValidation";
import { usePasswordVisibility } from "../hooks/usePasswordVisibility";

type TranslationKeys = keyof typeof translations.en;

interface ResetPasswordFormProps {
  formData: {
    password: string;
    newPassword: string;
  };
  loading: boolean;
  error: string | null;
  success: boolean;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSubmit: (e: React.FormEvent) => void;
}

const ResetPasswordForm: React.FC<ResetPasswordFormProps> = ({
  formData,
  loading,
  error,
  success,
  handleChange,
  handleSubmit,
}) => {
  const { t } = useTranslation();
  const { visibilityStates, toggleVisibility } = usePasswordVisibility({
    initialStates: {
      currentPassword: false,
      newPassword: false,
      confirmPassword: false,
    },
  });

  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [validationErrors, setValidationErrors] = React.useState<string[]>([]);

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmPassword(e.target.value);
  };

  const validateForm = (): boolean => {
    const passwordValidation = validatePassword(formData.newPassword);
    const passwordsMatch = doPasswordsMatch(formData.newPassword, confirmPassword);

    const errors: string[] = [...passwordValidation.errors];
    if (!passwordsMatch) {
      errors.push("Passwords do not match");
    }

    setValidationErrors(errors);
    return errors.length === 0;
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      handleSubmit(e);
    }
  };

  if (loading) {
    return <LoadingState message="Resetting password..." />;
  }

  return (
    <form onSubmit={handleFormSubmit}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {t("Password has been reset successfully. Redirecting..." as TranslationKeys)}
        </Alert>
      )}

      {validationErrors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body2" component="div">
            {validationErrors.map((error) => (
              <div key={error}>{t(error as TranslationKeys)}</div>
            ))}
          </Typography>
        </Alert>
      )}

      <PasswordInput
        label="Current Password"
        name="password"
        value={formData.password}
        onChange={handleChange}
        showPassword={visibilityStates.currentPassword}
        setShowPassword={() => toggleVisibility("currentPassword")}
        disabled={loading}
        sx={{ mb: 2 }}
      />

      <PasswordInput
        label="New Password"
        name="newPassword"
        value={formData.newPassword}
        onChange={handleChange}
        showPassword={visibilityStates.newPassword}
        setShowPassword={() => toggleVisibility("newPassword")}
        disabled={loading}
        sx={{ mb: 2 }}
      />

      <PasswordInput
        label="Confirm New Password"
        name="confirmPassword"
        value={confirmPassword}
        onChange={handleConfirmPasswordChange}
        showPassword={visibilityStates.confirmPassword}
        setShowPassword={() => toggleVisibility("confirmPassword")}
        disabled={loading}
        sx={{ mb: 3 }}
      />

      <Button type="submit" variant="contained" fullWidth disabled={loading}>
        {loading ? <CircularProgress size={24} /> : t("Reset Password" as TranslationKeys)}
      </Button>
    </form>
  );
};

export default ResetPasswordForm;
